import _defineProperty from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import React, { Fragment } from 'react';
import classNames from 'classnames';
import clamp from 'lodash/clamp';
import analyticsUtils from '../../helpers/analytics-utils';
import { throttle } from '../../helpers';
import CosmeticScrollbar from '../cosmetic-scrollbar';
import style from './index.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const THROTTLE_SCROLL = 250;

const setScrollWithoutTransition = (elem, scrollPos) => {
  // Temporarily disable smooth scrolling while setting the new scroll position
  elem.style.scrollBehavior = 'auto';
  elem.scrollLeft = scrollPos;
  elem.style.scrollBehavior = '';
};

class HorizontalScrollWrapper extends React.Component {
  constructor(props) {
    var _this;

    super(props);
    _this = this;

    // One-off event
    _defineProperty(this, "trackScroll", () => {
      if (this.props.eventScrollThreshold !== undefined && this.scrollNode.current.scrollLeft > this.props.eventScrollThreshold && !this.state.hasTrackedScroll) {
        const {
          eventCategory,
          eventAction
        } = this.props;
        const hasAnalyticsEvent = Boolean(eventCategory && eventAction);

        if (hasAnalyticsEvent) {
          this.setState({
            hasTrackedScroll: true
          });
          analyticsUtils.gaTagTracking({
            event: analyticsUtils.events.MODULE_IMPRESSION,
            eventCategory,
            eventAction
          });
        }
      }
    });

    _defineProperty(this, "trackScrollDirection", direction => {
      const {
        eventCategory,
        eventAction
      } = this.props;
      analyticsUtils.gaTagTracking({
        event: analyticsUtils.events.MODULE_IMPRESSION,
        eventCategory,
        eventAction,
        eventLabel: direction === 'forward' ? analyticsUtils.labels.ARROW_RIGHT : analyticsUtils.labels.ARROW_LEFT
      });
    });

    _defineProperty(this, "setScroll", function (scrollPos) {
      let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      const {
        immediate = false
      } = options;
      const elem = _this.scrollNode.current;

      if (immediate) {
        setScrollWithoutTransition(elem, scrollPos);
      } else {
        elem.scrollLeft = scrollPos;
      }

      _this.handleScroll(); // Setting scrollLeft doesn't always trigger the scroll event

    });

    _defineProperty(this, "handleScroll", () => {
      const elem = this.scrollNode.current;

      if (!elem) {
        return;
      }

      const containerWidth = elem.clientWidth;
      const scrollLeft = elem.scrollLeft;
      const contentWidth = elem.scrollWidth;
      const showLeftElem = scrollLeft > 0;
      const showRightElem = scrollLeft + containerWidth < contentWidth;
      this.lastScrollPos = scrollLeft;
      this.trackScroll();

      if (showLeftElem !== this.state.showLeftElem || showRightElem !== this.state.showRightElem) {
        this.setState({
          showLeftElem,
          showRightElem
        });
      }

      if (this.props.useCosmeticScrollbar) {
        this.setCosmeticScrollbarPosition(scrollLeft, containerWidth, contentWidth);
      }
    });

    _defineProperty(this, "scrollPadding", () => {
      const elem = this.scrollNode.current;

      if (!elem) {
        return 0;
      }

      const elemStyle = window.getComputedStyle(elem);
      return (parseFloat(elemStyle.paddingLeft) || 0) + (parseFloat(elemStyle.paddingRight) || 0);
    });

    _defineProperty(this, "scrollPageSize", () => {
      const {
        extraScroll
      } = this.props;
      const elem = this.scrollNode.current;

      if (!elem) {
        return 0;
      }

      const visibleWidth = elem.clientWidth - this.scrollPadding();
      const contentWidth = elem.scrollWidth;
      const pages = contentWidth / visibleWidth;
      return contentWidth / pages + extraScroll;
    });

    _defineProperty(this, "moveScroll", direction => {
      const elem = this.scrollNode.current;
      const sizePerPg = this.scrollPageSize();
      let scrollPos = elem.scrollLeft;

      if (direction === 'forward') {
        scrollPos += sizePerPg;
      } else {
        scrollPos -= sizePerPg;
      }

      elem.scrollLeft = scrollPos;
      this.trackScrollDirection(direction);
    });

    this.state = {
      showLeftElem: false,
      showRightElem: true,
      hasTrackedScroll: false,
      cosmeticScrollbarPos: props.useCosmeticScrollbar ? 0 : null
    };
    this.lastScrollPos = 0; // Not stored in state to avoid re-renders while scrolling

    this.scrollNode = /*#__PURE__*/React.createRef();
  }

  componentDidMount() {
    this.handleScroll();
  }

  getScroll() {
    return this.lastScrollPos;
  }

  setCosmeticScrollbarPosition(scrollPos, visibleWidth, contentWidth) {
    const maxPosition = contentWidth - visibleWidth;
    const positionAsPercentage = Math.round(scrollPos / maxPosition * 100);
    /*
     * touch devices can make scroll requests beyond min/max
     * that 'snap' back when touch is released
     */

    const percentage = clamp(positionAsPercentage, 0, 100);
    this.setState({
      cosmeticScrollbarPos: percentage || 0
    });
  }

  render() {
    const {
      cosmeticScrollbarPos,
      showLeftElem,
      showRightElem
    } = this.state;
    const {
      children,
      className,
      contentClassName,
      leftElem,
      rightElem,
      useCosmeticScrollbar
    } = this.props;
    const showCosmeticScrollbar = useCosmeticScrollbar && (showLeftElem || showRightElem);
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsxs("div", {
        className: classNames(style.HorizontalScrollWrapper, style.evolved, className),
        "data-id": "horizontal-scroll-wrapper_index_Fragment_div",
        children: [Boolean(leftElem) && leftElem({
          isVisible: showLeftElem,
          onClick: () => this.moveScroll('back')
        }), /*#__PURE__*/_jsx("div", {
          className: style.scrollContentWrapper,
          "data-id": "horizontal-scroll-wrapper_index_div_div_1",
          children: /*#__PURE__*/_jsx("div", {
            ref: this.scrollNode,
            className: classNames(style.scrollContent, contentClassName),
            onScroll: throttle(this.handleScroll, THROTTLE_SCROLL),
            "data-id": "horizontal-scroll-wrapper_index_div_div_2",
            children: children
          })
        }), Boolean(rightElem) && rightElem({
          isVisible: showRightElem,
          onClick: () => this.moveScroll('forward')
        })]
      }), showCosmeticScrollbar && /*#__PURE__*/_jsx("div", {
        className: classNames(style.cosmeticWrapper, style.evolved),
        "data-id": "horizontal-scroll-wrapper_index_div_3",
        children: /*#__PURE__*/_jsx("div", {
          className: style.cosmeticInner,
          "data-id": "horizontal-scroll-wrapper_index_div_div_4",
          children: /*#__PURE__*/_jsx(CosmeticScrollbar, {
            lightTheme: false,
            position: cosmeticScrollbarPos,
            "data-id": "horizontal-scroll-wrapper_index_div_CosmeticScrollbar"
          })
        })
      })]
    });
  }

}

HorizontalScrollWrapper.defaultProps = {
  extraScroll: 0,
  useCosmeticScrollbar: true
};
export default HorizontalScrollWrapper;